import { FC, useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { feventListAgencyAdminStateSelectorFamily } from '@shared/api/fevent/fevent.selector';
import { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { Loading } from '@shared/components/Loading';
import { ReloadButton } from '@shared/components/ReloadButton';
import StatPageSideBar from '@shared/components/StatPageSideBar';
import SearchBar from '@shared/containers/SearchBar/SearchBar';
import { searchTextAtomFamily } from '@shared/containers/SearchBar/state/searchBar.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { RoughRegionEnum } from '@shared/utils/getRoughRegion';
import { useMediaQuery } from 'react-responsive';
import { useParams, useSearchParams } from 'react-router-dom';

import AgencyAdminBugoListTable from '../containers/AgencyAdminBugoListTable/AgencyAdminBugoListTable';
import AgencyAdminBugoListTableMobile from '../containers/AgencyAdminBugoListTable/AgencyAdminBugoListTableMobile';
import { useTitleHook } from '../hooks/useTitleHook';

export enum BugoStatRangeQueryEnum {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Year = 'year',
}

const SEARCH_TEXT_ID = 'agencyAdminBugoStatPage';

const AgencyAdminBugoStatPage: FC = function AgencyAdminBugoStatPage() {
  const { bugoBrandId } = useParams();
  const bugoBrandId_ = bugoBrandId ?? 'undefined';
  useTitleHook('관리자용 페이지', undefined, undefined, true, '/');

  const feventListState = useRecoilValue(
    feventListAgencyAdminStateSelectorFamily(bugoBrandId_),
  );

  const [searchParams, _] = useSearchParams();
  const range = searchParams.get('range') as BugoStatRangeQueryEnum | null;

  const searchText = useRecoilValue(searchTextAtomFamily(SEARCH_TEXT_ID));

  //*mobile 기준
  const isMobile = !useMediaQuery({
    query: '(min-width: 1024px)',
  });

  const { userProfile, bugoRole } = useAuth();

  const teamTypesFilter = useMemo(() => {
    if (bugoRole() === LowerRole.BugoAgencyBranchAdmin) {
      return null;
    } else {
      const bugoBrand = userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];
      const teamTypes = bugoBrand?.bugoAgency?.teamTypes ?? [];
      const result: CheckBoxFilter = {};
      teamTypes.forEach((teamType) => (result[teamType] = true));
      return result;
    }
  }, [bugoRole, userProfile?.bugoAgencyAdminDetail?.bugoBrands]);

  const regionsFilter = useMemo(() => {
    const roughRegions = Object.keys(RoughRegionEnum);
    const result: CheckBoxFilter = {};
    roughRegions.forEach((region) => (result[region] = true));

    return result;
  }, []);

  const table = useMemo(() => {
    if (feventListState.status === 'success' && feventListState.data) {
      return (
        <AgencyAdminBugoListTable
          feventList={feventListState.data}
          range={range}
          teamTypesFilter={teamTypesFilter}
          regionsFilter={regionsFilter}
        />
      );
    } else {
      return (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      );
    }
  }, [feventListState, range, teamTypesFilter, regionsFilter]);

  if (isMobile) {
    return (
      <div>
        {/* Search Bar */}
        <div className="p-4">
          <SearchBar
            searchTextAtomId={SEARCH_TEXT_ID}
            placeholder="소속/지도사성함/지역/회원번호로 검색"
          />
        </div>
        {feventListState.status === 'success' && feventListState.data ? (
          <AgencyAdminBugoListTableMobile
            feventList={feventListState.data}
            teamTypesFilter={teamTypesFilter}
            range={range}
            searchValue={searchText}
            regionsFilter={regionsFilter}
          />
        ) : (
          <div className="center-box h-screen-15">
            <Loading />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="flex">
        {/* Sidebar */}
        <div className="flex flex-col">
          <StatPageSideBar />
        </div>
        <div className="mx-auto max-w-screen-2xl flex-1">
          <div className="py-8 px-10">
            <div className="flex justify-between space-x-10">
              <div>
                <p className="text-20 font-bold">부고 등록 내역 조회</p>
                <p className="font-sm pt-2 text-gray-500">
                  조건을 설정하여 조회할 수 있습니다.(최대 60일 기간 데이터 조회 가능)
                </p>
              </div>
              <div className="center-box h-full gap-4">
                <ReloadButton />
              </div>
            </div>
            <div className="pt-4">{table}</div>
          </div>
        </div>
      </div>
    );
  }
};

export default AgencyAdminBugoStatPage;
